import { useState } from 'react'
import { Users } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { DEFAULT_MAX_PARTY_SIZE } from '../lib/partySize'
import PartySizeStepper from './PartySizeStepper'
import { showToast } from './Toast'
import { api } from '../services/api'
import LanguageSwitcher from './LanguageSwitcher'

/**
 * #968 セルフオーダーQR入店の導入画面（1画面）。店舗画像＋店名＋テーブル番号＋
 * 言語切替（ヒーロー）と、来店人数の入力（−/＋・必須）＋「メニューへ進む」を 1 枚に
 * まとめる。初回スキャン時のみ表示し、進む で卓セッションを作成（着席）して onComplete。
 * 再利用: LanguageSwitcher / 人数ピッカーは ChangePartySizeModal と同パターン /
 * hero 画像は StoreDetail と同パターン。
 */
interface Props {
  storeId: string
  storeName: string
  storeImageUrl?: string
  tableNumber: string
  /** 卓トークン(?t=)。セッション作成時に卓を検証するため必須。 */
  token: string
  /** REQ-28 (#1536) 1 組の最大人数（店舗設定）。既定は lib/partySize.ts の 50。 */
  maxPartySize?: number
  onComplete: (partySize: number) => void
}

const MIN = 1

export default function SelfOrderWelcome({
  storeId, storeName, storeImageUrl, tableNumber, token,
  maxPartySize: MAX = DEFAULT_MAX_PARTY_SIZE, onComplete,
}: Props) {
  const { t } = useI18n()
  const [partySize, setPartySize] = useState(2)
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      const res = await api.createTableSession(storeId, token, partySize)
      onComplete(typeof res?.partySize === 'number' ? res.partySize : partySize)
    } catch {
      showToast({ type: 'error', message: t('selfOrderWelcome.failed') })
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[120] bg-white flex flex-col safe-top safe-bottom animate-fade-in">
      {/* ヒーロー: 店舗画像 + 店名 + テーブル番号 + 言語切替 */}
      <div className="relative h-[32vh] min-h-[180px] bg-navy shrink-0">
        {storeImageUrl && (
          <img
            src={storeImageUrl}
            alt={storeName}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
        <div className="absolute top-0 right-0 p-3">
          <LanguageSwitcher />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
          <p className="font-body text-sm opacity-90">{t('selfOrderWelcome.greeting')}</p>
          <h1 className="font-display text-2xl font-bold mt-1 leading-tight">{storeName}</h1>
          <span className="inline-flex items-center mt-2 rounded-full bg-white/20 backdrop-blur px-3 py-1 text-sm font-display font-semibold">
            {t('selfOrderWelcome.tableLabel', { table: tableNumber })}
          </span>
        </div>
      </div>

      {/* 人数入力（−/＋・必須） */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <Users size={30} className="text-yokoso-red mb-3" />
        <p className="text-center font-display text-lg font-semibold text-text-primary">
          {t('selfOrderWelcome.partyQuestion')}
        </p>

        {/* #1583 ± だけだと 130 名にするのに 128 回タップになる。数字を押すと
          * 直接入力に切り替わる (少人数の客の操作は従来どおり)。 */}
        <div className="mt-8 flex items-center justify-center">
          <PartySizeStepper
            value={partySize}
            min={MIN}
            max={MAX}
            onChange={setPartySize}
            size="lg"
          />
        </div>
        <p className="mt-3 font-body text-sm text-text-secondary">
          {t('selfOrderWelcome.people', { count: partySize })}
        </p>
      </div>

      {/* 進む（人数を確定してメニューへ） */}
      <div className="p-6 shrink-0">
        <button
          onClick={submit}
          disabled={submitting}
          className="w-full h-14 rounded-full bg-navy text-white font-display font-semibold text-base tap-effect active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          {submitting ? '...' : t('selfOrderWelcome.ok')}
        </button>
      </div>
    </div>
  )
}
